import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import "./CheckinForm.css";
import "./RoomForm.css";
import "./MeterForm.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const METERS = [
  { field: "reading_gas", label: "Gas", unit: "m³" },
  { field: "reading_electricity", label: "Electric", unit: "kWh" },
  { field: "reading_electricity_low", label: "Electric (low)", unit: "kWh" },
  { field: "reading_electricity_high", label: "Electric (high)", unit: "kWh" },
  { field: "reading_water", label: "Water", unit: "m³" },
  { field: "reading_heat_pump", label: "Heat pump", unit: "GJ" },
];

const SAVE_DEBOUNCE_MS = 800;

const EMPTY_READINGS = Object.fromEntries(
  METERS.map((meter) => [meter.field, ""])
);

const formatTime = (date) =>
  date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });

// The readings are floats on the server but text in the field, so a half-typed
// "12." survives until the user finishes the number.
const toField = (value) =>
  value === null || value === undefined ? "" : String(value);

const toReading = (value) => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const number = Number(trimmed);
  return Number.isFinite(number) ? number : null;
};

// Digits and a single decimal point — a meter cannot read minus, and letting
// through anything else only turns into a reading that will not save.
const sanitise = (raw) => {
  const [whole, ...rest] = raw.replace(/[^\d.,]/g, "").replace(/,/g, ".").split(".");
  return rest.length > 0 ? `${whole}.${rest.join("")}` : whole;
};

const MeterForm = () => {
  const { cfid } = useParams();
  const navigate = useNavigate();

  const { sendRequest: getCheckinRequest, isLoading, error } = useHttpNoAuth();
  const {
    sendRequest: saveReadingsRequest,
    isLoading: isSaving,
    error: saveError,
  } = useHttpNoAuth();
  const {
    sendRequest: uploadPhotosRequest,
    isLoading: isUploading,
    error: uploadError,
  } = useHttpNoAuth();
  const {
    sendRequest: deletePhotoRequest,
    isLoading: isDeletingPhoto,
    error: deletePhotoError,
  } = useHttpNoAuth();

  const [address, setAddress] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [readings, setReadings] = useState(EMPTY_READINGS);
  const [photos, setPhotos] = useState([]);

  const [savedAt, setSavedAt] = useState(null);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  // How many thumbs to hold open while the upload is in flight.
  const [uploadingCount, setUploadingCount] = useState(0);

  // What the server currently holds — nothing is sent while the form matches it.
  const baselineRef = useRef(null);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  const loadCheckin = useCallback(
    (onLoaded) => {
      if (!cfid) return;
      getCheckinRequest(
        { url: `${BACKEND_URL}/welocate/checkins/${cfid}`, method: "GET" },
        (data) => {
          onLoaded?.();

          setAddress(
            [data?.street, data?.number, data?.floor].filter(Boolean).join(" ")
          );

          const fetched = Object.fromEntries(
            METERS.map((meter) => [meter.field, toField(data?.[meter.field])])
          );

          setReadings(fetched);
          // The check-in keeps the meter photos apart from the room ones.
          setPhotos(data?.meter_photos ?? []);
          // Only the fields the user has not touched yet may be overwritten by
          // a refetch — an upload must not undo a reading typed a moment ago.
          if (!baselineRef.current) baselineRef.current = fetched;
          setLoaded(true);
        }
      );
    },
    [cfid, getCheckinRequest]
  );

  useEffect(() => {
    loadCheckin();
  }, [loadCheckin]);

  // Recomputed every render: the baseline lives in a ref and moves on each save.
  const base = baselineRef.current;
  const isDirty = Boolean(
    base && METERS.some((meter) => base[meter.field] !== readings[meter.field])
  );

  const saveReadings = useCallback(() => {
    const sent = { ...readings };
    return saveReadingsRequest(
      {
        url: `${BACKEND_URL}/welocate/checkins/${cfid}`,
        method: "PATCH",
        body: Object.fromEntries(
          METERS.map((meter) => [meter.field, toReading(sent[meter.field])])
        ),
      },
      () => {
        baselineRef.current = sent;
        setSavedAt(new Date());
      }
    );
  }, [readings, cfid, saveReadingsRequest]);

  // Autosave: the design has no save button for the fields, only a "done" exit.
  useEffect(() => {
    if (!isDirty) return undefined;
    const timer = setTimeout(saveReadings, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isDirty, saveReadings]);

  const setReading = (field, raw) =>
    setReadings((current) => ({ ...current, [field]: sanitise(raw) }));

  const uploadPhotos = (fileList) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;

    const formData = new FormData();
    formData.append("checkin_id", cfid);
    files.forEach((file) => formData.append("files", file));

    setUploadingCount(files.length);
    uploadPhotosRequest(
      {
        url: `${BACKEND_URL}/welocate/checkins/${cfid}/meter-photos`,
        method: "POST",
        body: formData,
        isFileUpload: true,
      },
      () => {
        setSavedAt(new Date());
        // The skeletons stay up over the refetch, so they hand straight over
        // to the real thumbs instead of blinking out in between.
        loadCheckin(() => setUploadingCount(0));
      }
    );
  };

  // The hook never rejects, so a failed upload — or a failed refetch after a
  // good one — has to clear the skeletons here.
  useEffect(() => {
    if (uploadError || error) setUploadingCount(0);
  }, [uploadError, error]);

  const pickPhotos = (event) => {
    uploadPhotos(event.target.files);
    // Let the same file be picked again after an upload or a cancel.
    event.target.value = "";
  };

  const deletePhoto = (photoId) => {
    if (isDeletingPhoto) return;
    setDeletingPhotoId(photoId);
    deletePhotoRequest(
      { url: `${BACKEND_URL}/welocate/photos/${photoId}`, method: "DELETE" },
      () => {
        setDeletingPhotoId(null);
        setPhotos((current) => current.filter((photo) => photo.id !== photoId));
        setSavedAt(new Date());
      }
    );
  };

  // The hook never rejects, so a failed delete has to release the thumb here.
  useEffect(() => {
    if (deletePhotoError) setDeletingPhotoId(null);
  }, [deletePhotoError]);

  const finish = async () => {
    if (isDirty) await saveReadings();
    navigate(`/checkin/${cfid}`);
  };

  const isBusy = isSaving || isUploading;
  const hasFailed = !isBusy && Boolean(saveError || uploadError);

  // Edits are only in the browser until the debounce fires and the PATCH lands,
  // so the way out stays shut until then. A failed save reopens it — the error
  // is on screen and stranding the user on the page helps nobody.
  const isSavePending = (isDirty || isSaving) && !saveError;

  return (
    <div className="cf-page">
      <header className="cf-topbar">
        <button
          type="button"
          className="cf-back"
          aria-label={isSavePending ? "Saving — please wait" : "Back"}
          aria-busy={isSavePending}
          disabled={isSavePending}
          onClick={() => navigate(`/checkin/${cfid}`)}
        >
          {isSavePending ? <span className="cf-spinner" /> : <ChevronLeftIcon />}
        </button>

        <div className="cf-topbar-titles">
          <h1 className="cf-topbar-title">Meter readings</h1>
          <p className="cf-topbar-sub">{address}</p>
        </div>

        {(isBusy || hasFailed || savedAt) && (
          <span
            className={`cf-saved${hasFailed ? " rf-saved--error" : ""}`}
            role="status"
          >
            {isBusy && "Saving…"}
            {hasFailed && "Not saved"}
            {!isBusy && !hasFailed && (
              <>
                <CheckCircleOutlineIcon />
                Saved <span className="cf-saved-time">{formatTime(savedAt)}</span>
              </>
            )}
          </span>
        )}
      </header>

      {isLoading && !loaded && <p className="cf-state">Loading meters…</p>}

      {!isLoading && error && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">{error}</p>
          <button type="button" className="cf-state-retry" onClick={loadCheckin}>
            Try again
          </button>
        </div>
      )}

      {loaded && (
        <main className="cf-main">
          <section className="cf-card mf-readings">
            {METERS.map((meter) => (
              <div className="mf-reading" key={meter.field}>
                <label className="mf-reading-label" htmlFor={`mf-${meter.field}`}>
                  {meter.label}
                </label>

                <div className="mf-input">
                  <input
                    id={`mf-${meter.field}`}
                    className="mf-input-field"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="—"
                    value={readings[meter.field]}
                    onChange={(event) =>
                      setReading(meter.field, event.target.value)
                    }
                  />
                  <span className="mf-input-unit">{meter.unit}</span>
                </div>
              </div>
            ))}
          </section>

          <section className="cf-card rf-block">
            <div className="rf-photos-head">
              <h3 className="cf-section-title">METER PHOTOS</h3>
              <span className="rf-photos-count">
                {uploadingCount > 0
                  ? `Uploading ${uploadingCount}…`
                  : `${photos.length} stored`}
              </span>
            </div>

            <div className="rf-choices">
              <button
                type="button"
                className="rf-photo-btn"
                disabled={isUploading}
                onClick={() => cameraInputRef.current?.click()}
              >
                <PhotoCameraOutlinedIcon />
                Photograph
              </button>
              <button
                type="button"
                className="rf-photo-btn"
                disabled={isUploading}
                onClick={() => libraryInputRef.current?.click()}
              >
                <CollectionsOutlinedIcon />
                Library
              </button>
            </div>

            <input
              ref={cameraInputRef}
              className="rf-file-input"
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={pickPhotos}
            />
            <input
              ref={libraryInputRef}
              className="rf-file-input"
              type="file"
              accept="image/*"
              multiple
              onChange={pickPhotos}
            />

            {(photos.length > 0 || uploadingCount > 0) && (
              <ul className="rf-thumbs">
                {photos.map((photo) => (
                  <li key={photo.id} className="rf-thumb">
                    <img
                      src={photo.photo_blob_url}
                      alt={photo.comments || "Meter"}
                    />
                    <span className="rf-thumb-stored" aria-label="Uploaded">
                      <CheckIcon />
                    </span>
                    <button
                      type="button"
                      className="rf-thumb-delete"
                      aria-label="Delete meter photo"
                      disabled={isDeletingPhoto}
                      onClick={() => deletePhoto(photo.id)}
                    >
                      {deletingPhotoId === photo.id ? (
                        <span className="cf-spinner rf-thumb-spinner" />
                      ) : (
                        <CloseIcon />
                      )}
                    </button>
                  </li>
                ))}

                {Array.from({ length: uploadingCount }, (_, index) => (
                  <li
                    key={`pending-${index}`}
                    className="rf-thumb rf-thumb--pending"
                    aria-label="Uploading photo"
                  />
                ))}
              </ul>
            )}

            {!isUploading && uploadError && (
              <p className="rf-error" role="alert">
                {uploadError}
              </p>
            )}

            {!isDeletingPhoto && deletePhotoError && (
              <p className="rf-error" role="alert">
                {deletePhotoError}
              </p>
            )}
          </section>

          <p className="cf-note">
            Leave a meter empty when the property does not have one — an empty
            field is not a missing reading.
          </p>

          {!isSaving && saveError && (
            <p className="rf-error" role="alert">
              {saveError}
            </p>
          )}
        </main>
      )}

      {loaded && (
        <footer className="cf-actions">
          <div className="cf-actions-inner">
            <button
              type="button"
              className="cf-btn cf-btn--primary"
              disabled={isSaving}
              onClick={finish}
            >
              <CheckIcon />
              Done with meters
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default MeterForm;
